/**
 * Owners: invite links for the team, each giving one role (once only or reusable), working for
 * a while (two weeks unless chosen) and for some number of people at most; copying and
 * withdrawing them. Whoever opens one signs in or makes an account, then joins.
 */
import { For, createSignal, type JSX } from "solid-js";
import type { Role } from "@protocol/Role";
import type { TeamView } from "@protocol/TeamView";
import { inviteText } from "../client/space";
import { ROLES } from "../state/access";
import { toast } from "../shared/toast";
import { copyText, formatDate } from "../shared/ui";
import { usePages } from "./context";

/** What each role may do, in a few words. */
const ROLE_HINTS: Record<Role, string> = {
  viewer: "sees the spaces and the others",
  commenter: "sees and comments",
  editor: "changes the spaces, makes new ones",
  owner: "everything, and who is in the team",
};

/** How long a new invite may work, in days (`0`: until withdrawn). */
const DURATIONS: [number, string][] = [
  [1, "1 day"],
  [7, "7 days"],
  [14, "14 days"],
  [30, "30 days"],
  [0, "until withdrawn"],
];

/** The invites of `team`. */
export function InvitesList(props: { team: TeamView }): JSX.Element {
  const { accounts, server } = usePages();
  const [role, setRole] = createSignal<Role>("editor");
  const [once, setOnce] = createSignal(false);
  const [days, setDays] = createSignal(14);
  const [uses, setUses] = createSignal("");
  /** The link of invite `code`. */
  const link = (code: string) => inviteText({ server, code });
  /** Copies an invite link and says so. */
  const copy = async (code: string) => toast((await copyText(link(code))) ? "Invite link copied." : "Copy is blocked here: select the link instead.");
  return (
    <section class="team-section">
      <h3>Invite people</h3>
      <div class="row-inline">
        <select class="field" id="invite-role" value={role()} onChange={(e) => setRole(e.currentTarget.value as Role)}>
          <For each={ROLES}>{(r) => <option value={r}>{r}</option>}</For>
        </select>
        <label class="small">
          <input type="checkbox" id="invite-once" checked={once()} onChange={(e) => setOnce(e.currentTarget.checked)} /> once only
        </label>
        <select class="field" id="invite-days" title="How long the link works" value={days()} onChange={(e) => setDays(Number(e.currentTarget.value))}>
          <For each={DURATIONS}>{([value, label]) => <option value={value}>{label}</option>}</For>
        </select>
        <input class="field" id="invite-uses" type="number" min="1" placeholder="people (any)" title="How many people may join with it" value={uses()} disabled={once()} onInput={(e) => setUses(e.currentTarget.value)} />
        <button
          type="button"
          class="btn primary"
          id="invite-create"
          onClick={async () => {
            const maxUses = Number(uses()) > 0 ? Math.floor(Number(uses())) : undefined;
            const invite = await accounts.invite(server, props.team.id, role(), once(), { days: days(), ...(maxUses ? { maxUses } : {}) });
            if (invite) void copy(invite.code);
          }}
        >
          Create link
        </button>
      </div>
      <p class="small muted">Each {role()} {ROLE_HINTS[role()]}.</p>
      <ul class="invite-list">
        <For each={props.team.invites} fallback={<li class="muted small">No invite link yet.</li>}>
          {(invite) => (
            <li data-invite={invite.code} data-invite-role={invite.role}>
              <span class="role-pill">{invite.role}</span>
              <span class="mono small invite-link" data-invite-link>
                {link(invite.code)}
              </span>
              <span class="muted small">
                {invite.once ? "once" : invite.max_uses ? `${invite.max_uses - invite.used} of ${invite.max_uses} left` : "reusable"} · used {invite.used} · {formatDate(invite.created)}
                {invite.expires ? ` · until ${formatDate(invite.expires)}` : ""}
              </span>
              <span class="btnrow">
                <button type="button" class="mini" data-invite-copy onClick={() => void copy(invite.code)}>
                  Copy
                </button>
                <button type="button" class="mini" data-invite-revoke onClick={() => void accounts.revoke(server, props.team.id, invite.code)}>
                  Withdraw
                </button>
              </span>
            </li>
          )}
        </For>
      </ul>
    </section>
  );
}
